import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Edit, Trash2, Shield, Users, Eye, Search, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';

const allPermissions = [
  { group: 'Clients', permissions: ['clients.view', 'clients.create', 'clients.edit', 'clients.delete'] },
  { group: 'Leads', permissions: ['leads.view', 'leads.create', 'leads.edit', 'leads.delete'] },
  { group: 'Projects', permissions: ['projects.view', 'projects.create', 'projects.edit', 'projects.delete', 'projects.view_all', 'projects.edit_all'] },
  { group: 'Tasks', permissions: ['tasks.view', 'tasks.create', 'tasks.edit', 'tasks.delete', 'tasks.view_all', 'tasks.edit_all'] },
  { group: 'Tickets', permissions: ['tickets.view', 'tickets.create', 'tickets.edit', 'tickets.delete'] },
  { group: 'Finance', permissions: ['invoices.view', 'invoices.create', 'invoices.edit', 'invoices.delete', 'expenses.view', 'expenses.create', 'expenses.edit', 'expenses.delete', 'payments.view', 'payments.create', 'payments.edit', 'payments.delete', 'payment_links.view', 'payment_links.edit'] },
  { group: 'HR', permissions: ['employees.view', 'employees.create', 'employees.edit', 'employees.delete', 'leaves.view', 'leaves.approve', 'eod_notes.view_all', 'attendance.view', 'attendance.create', 'attendance.edit', 'attendance.delete', 'payroll.view', 'payroll.create', 'payroll.edit', 'payroll.delete'] },
  { group: 'Assets', permissions: ['domains.view', 'domains.create', 'domains.edit', 'domains.delete', 'hosting.view', 'hosting.create', 'hosting.edit', 'hosting.delete', 'ssl_certificates.view', 'ssl_certificates.create', 'ssl_certificates.edit', 'ssl_certificates.delete', 'amc_contracts.view', 'amc_contracts.create', 'amc_contracts.edit', 'amc_contracts.delete', 'bank_accounts.view', 'bank_accounts.create', 'bank_accounts.edit', 'bank_accounts.delete', 'dlt.view', 'dlt.create', 'dlt.edit', 'dlt.delete'] },
  { group: 'Credentials Vault', permissions: ['credentials.view', 'credentials.create', 'credentials.edit', 'credentials.delete', 'credentials.manage_access'] },
  { group: 'Myzow SMB', permissions: ['smb.view', 'smb.create', 'smb.edit', 'smb.delete', 'smb.performance', 'smb.assignment_rules', 'smb.outreach'] },
  { group: 'WhatsApp Inbox', permissions: ['whatsapp.inbox.smb', 'whatsapp.inbox.general'] },
  { group: 'System', permissions: ['users.view', 'users.create', 'roles.view', 'roles.create', 'settings.view', 'migration.run'] },
];

const totalPermissionCount = allPermissions.reduce((sum, g) => sum + g.permissions.length, 0);

// Renders a permission key like "eod_notes.view_all" as "EOD Notes View All"
// (also handles plain keys like "leaves.approve" → "Leaves Approve").
const PERMISSION_LABEL_OVERRIDES = {
  'smb.view': 'SMB View',
  'smb.create': 'SMB Create',
  'smb.edit': 'SMB Edit',
  'smb.delete': 'SMB Delete',
  'smb.performance': 'SMB Performance',
  'smb.assignment_rules': 'SMB Assignment Rules',
  'smb.outreach': 'SMB Outreach Automation',
  'whatsapp.inbox.smb': 'SMB Inbox',
  'whatsapp.inbox.general': 'General Inbox',
  'migration.run': 'Run Legacy Data Migration',
  'dlt.view': 'DLT View',
  'dlt.create': 'DLT Create',
  'dlt.edit': 'DLT Edit',
  'dlt.delete': 'DLT Delete',
  'ssl_certificates.view': 'SSL Certificates View',
  'ssl_certificates.create': 'SSL Certificates Create',
  'ssl_certificates.edit': 'SSL Certificates Edit',
  'ssl_certificates.delete': 'SSL Certificates Delete',
  'amc_contracts.view': 'AMC Contracts View',
  'amc_contracts.create': 'AMC Contracts Create',
  'amc_contracts.edit': 'AMC Contracts Edit',
  'amc_contracts.delete': 'AMC Contracts Delete',
  'bank_accounts.view': 'Bank Accounts View',
  'bank_accounts.create': 'Bank Accounts Create',
  'bank_accounts.edit': 'Bank Accounts Edit',
  'bank_accounts.delete': 'Bank Accounts Delete',
  'credentials.view': 'Credentials View',
  'credentials.create': 'Credentials Create',
  'credentials.edit': 'Credentials Edit',
  'credentials.delete': 'Credentials Delete',
  'credentials.manage_access': 'Manage Access (admin)',
  'tasks.view_all': 'View All Tasks (manager)',
  'tasks.edit_all': 'Edit/Delete Any Task (manager)',
  'projects.view_all': 'View All Projects (manager)',
  'projects.edit_all': 'Edit/Delete Any Project (manager)',
};

const formatPermissionLabel = (perm) => {
  if (PERMISSION_LABEL_OVERRIDES[perm]) return PERMISSION_LABEL_OVERRIDES[perm];
  const SPECIAL_CASE = { eod_notes: 'EOD Notes', hr: 'HR', smb: 'SMB' };
  return perm.split('.').map(part => {
    if (SPECIAL_CASE[part]) return SPECIAL_CASE[part];
    return part.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }).join(' ');
};

const mockRoles = [
  { id: 1, name: 'Super Admin', slug: 'super_admin', description: 'Full system access', users_count: 1, permissions: allPermissions.flatMap(g => g.permissions), is_system: true },
  { id: 2, name: 'Admin', slug: 'admin', description: 'Administrative access without system settings', users_count: 1, permissions: allPermissions.flatMap(g => g.permissions).filter(p => !p.startsWith('roles') && !p.startsWith('settings')), is_system: true },
  { id: 3, name: 'Project Manager', slug: 'project_manager', description: 'Manage projects and tasks', users_count: 1, permissions: ['clients.view', 'projects.view', 'projects.create', 'projects.edit', 'tasks.view', 'tasks.create', 'tasks.edit', 'tasks.delete'], is_system: true },
  { id: 4, name: 'Developer', slug: 'developer', description: 'View projects and manage tasks', users_count: 2, permissions: ['projects.view', 'tasks.view', 'tasks.create', 'tasks.edit'], is_system: true },
  { id: 5, name: 'HR Manager', slug: 'hr_manager', description: 'Manage employees and leaves', users_count: 1, permissions: ['employees.view', 'employees.create', 'leaves.view', 'leaves.approve', 'eod_notes.view_all'], is_system: false },
  { id: 6, name: 'Finance Manager', slug: 'finance_manager', description: 'Manage invoices and expenses', users_count: 0, permissions: ['invoices.view', 'invoices.create', 'expenses.view', 'expenses.create', 'clients.view'], is_system: false },
];

export const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const confirm = useConfirmation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ name: '', description: '', permissions: [] });

  useEffect(() => { fetchRoles(); }, []);
  const fetchRoles = async () => {
    try { const res = await api.get('/roles'); if (res.data.success) setRoles(res.data.data); } catch {}
  };

  const filteredRoles = roles.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Role name is required'); return; }
    try {
      if (isEditing && selectedRole) {
        await api.put(`/roles/${selectedRole.id}`, formData);
        toast.success('Role updated');
      } else {
        await api.post('/roles', formData);
        toast.success('Role created');
      }
      fetchRoles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Operation failed'); }
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDelete = async (role) => {
    if (role.is_system) { toast.error('Cannot delete system role'); return; }
    if (role.users_count > 0) { toast.error('Cannot delete role with assigned users. Reassign users first.'); return; }
    const confirmed = await confirm({ title: 'Delete Role', description: `Are you sure you want to delete role "${role.name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try { await api.delete(`/roles/${role.id}`); toast.success('Role deleted'); fetchRoles(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setFormData({ name: role.name, description: role.description, permissions: [...role.permissions] });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleView = (role) => {
    setSelectedRole(role);
    setIsViewOpen(true);
  };

  const togglePermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm]
    }));
  };

  const toggleGroupAll = (group) => {
    const groupPerms = group.permissions;
    const allChecked = groupPerms.every(p => formData.permissions.includes(p));
    setFormData(prev => ({
      ...prev,
      permissions: allChecked
        ? prev.permissions.filter(p => !groupPerms.includes(p))
        : [...new Set([...prev.permissions, ...groupPerms])]
    }));
  };

  const toggleAll = () => {
    const all = allPermissions.flatMap(g => g.permissions);
    const allChecked = all.every(p => formData.permissions.includes(p));
    setFormData(prev => ({ ...prev, permissions: allChecked ? [] : [...all] }));
  };

  const resetForm = () => { setFormData({ name: '', description: '', permissions: [] }); setSelectedRole(null); setIsEditing(false); };

  const customRolesCount = roles.filter(r => !r.is_system).length;
  const totalUsers = roles.reduce((sum, r) => sum + r.users_count, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="roles-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Roles & Permissions</h1><p className="text-muted-foreground">Configure access control for user roles</p></div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-role-btn"><Plus className="h-4 w-4 mr-2" />Create Role</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit Role' : 'Create Role'}</DialogTitle><DialogDescription>Define role name and assign permissions</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Role Name *</Label><Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Content Manager" required data-testid="role-name" /></div>
                <div className="space-y-2"><Label>Description</Label><Input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description of this role" /></div>
              </div>
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Permissions ({formData.permissions.length}/{totalPermissionCount})</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={toggleAll} className="text-xs h-7" data-testid="toggle-all-perms">
                    {allPermissions.flatMap(g => g.permissions).every(p => formData.permissions.includes(p)) ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                {allPermissions.map(group => {
                  const allGroupChecked = group.permissions.every(p => formData.permissions.includes(p));
                  const someGroupChecked = group.permissions.some(p => formData.permissions.includes(p)) && !allGroupChecked;
                  return (
                    <div key={group.group} className="p-4 rounded-lg bg-muted/20 border border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={allGroupChecked}
                            className={someGroupChecked ? 'data-[state=unchecked]:bg-primary/30' : ''}
                            onCheckedChange={() => toggleGroupAll(group)}
                            data-testid={`group-toggle-${group.group.toLowerCase()}`}
                          />
                          <span className="font-medium text-sm">{group.group}</span>
                          <Badge variant="outline" className="text-[10px] ml-1">{group.permissions.filter(p => formData.permissions.includes(p)).length}/{group.permissions.length}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 ml-6">
                        {group.permissions.map(perm => (
                          <div key={perm} className="flex items-center gap-2">
                            <Checkbox checked={formData.permissions.includes(perm)} onCheckedChange={() => togglePermission(perm)} id={perm} data-testid={`perm-${perm}`} />
                            <label htmlFor={perm} className="text-xs text-muted-foreground cursor-pointer">{formatPermissionLabel(perm)}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="role-submit-btn">{isEditing ? 'Update' : 'Create'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Shield className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold">{roles.length}</p><p className="text-xs text-muted-foreground">Total Roles</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold">{roles.filter(r => r.is_system).length}</p><p className="text-xs text-muted-foreground">System Roles</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><ShieldAlert className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{customRolesCount}</p><p className="text-xs text-muted-foreground">Custom Roles</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><Users className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold">{totalUsers}</p><p className="text-xs text-muted-foreground">Assigned Users</p></div></div></CardContent></Card>
      </div>

      {/* Search */}
      <Card><CardContent className="py-4">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search roles..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 max-w-sm" data-testid="role-search" /></div>
      </CardContent></Card>

      {/* Role Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredRoles.map(role => (
          <Card key={role.id} className="hover:border-primary/30 transition-colors" data-testid={`role-card-${role.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full ${role.is_system ? 'bg-blue-500/10' : 'bg-primary/10'} flex items-center justify-center`}>
                    <Shield className={`h-5 w-5 ${role.is_system ? 'text-blue-500' : 'text-primary'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-base">{role.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{role.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {role.is_system && <Badge variant="outline" className="text-xs mr-1">System</Badge>}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(role)} data-testid={`view-role-${role.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                  {!role.is_system && (
                    <>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(role)} data-testid={`edit-role-${role.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(role)} data-testid={`delete-role-${role.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm mb-3">
                <div className="flex items-center gap-1 text-muted-foreground"><Users className="h-3.5 w-3.5" /><span>{role.users_count} user{role.users_count !== 1 ? 's' : ''}</span></div>
                <div className="flex items-center gap-1 text-muted-foreground"><Shield className="h-3.5 w-3.5" /><span>{role.permissions.length}/{totalPermissionCount} permissions</span></div>
              </div>
              {/* Permission coverage bar */}
              <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-3">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(role.permissions.length / totalPermissionCount) * 100}%` }} />
              </div>
              <div className="flex flex-wrap gap-1">
                {allPermissions.map(g => {
                  const count = g.permissions.filter(p => role.permissions.includes(p)).length;
                  if (count === 0) return null;
                  return <Badge key={g.group} variant="outline" className={`text-[10px] px-1.5 py-0 ${count === g.permissions.length ? 'border-primary/40 text-primary' : ''}`}>{g.group} ({count}/{g.permissions.length})</Badge>;
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View Role Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Role Details — {selectedRole?.name}</DialogTitle><DialogDescription>View permissions assigned to this role</DialogDescription></DialogHeader>
          {selectedRole && <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full ${selectedRole.is_system ? 'bg-blue-500/10' : 'bg-primary/10'} flex items-center justify-center`}>
                <Shield className={`h-7 w-7 ${selectedRole.is_system ? 'text-blue-500' : 'text-primary'}`} />
              </div>
              <div>
                <h3 className="text-xl font-bold">{selectedRole.name}</h3>
                <p className="text-sm text-muted-foreground">{selectedRole.description}</p>
                <div className="flex items-center gap-3 mt-1">
                  {selectedRole.is_system && <Badge variant="outline">System Role</Badge>}
                  <span className="text-xs text-muted-foreground">{selectedRole.users_count} user{selectedRole.users_count !== 1 ? 's' : ''} assigned</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-border">
              <p className="text-sm font-medium mb-3">Permissions ({selectedRole.permissions.length}/{totalPermissionCount})</p>
              <div className="space-y-3">
                {allPermissions.map(group => {
                  const granted = group.permissions.filter(p => selectedRole.permissions.includes(p));
                  return (
                    <div key={group.group} className="p-3 rounded-lg bg-muted/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{group.group}</span>
                        <Badge variant={granted.length === group.permissions.length ? 'default' : 'outline'} className="text-[10px]">{granted.length}/{group.permissions.length}</Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {group.permissions.map(perm => {
                          const has = selectedRole.permissions.includes(perm);
                          return (
                            <div key={perm} className={`flex items-center gap-1.5 text-xs ${has ? 'text-emerald-500' : 'text-muted-foreground/40 line-through'}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${has ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                              <span>{formatPermissionLabel(perm)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
